import React, { useState } from 'react';
import {
  Sparkles,
  Palette,
  Sliders,
  Layers,
  RotateCcw,
  Copy,
  CheckCircle2,
  Type,
  Square,
  QrCode,
  Barcode,
  Image as ImageIcon,
  HelpCircle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { VdpElement, ElementMaterialFinish, ElementMaterialConfig, ElementMaterialBumpMap } from '../../types';
import { useThreeDContext } from '../../store/ThreeDContext';
import {
  MATERIAL_FINISH_PRESETS,
  QUICK_FOIL_SWATCHES
} from '../../utils/elementMaterialRenderer';
import { BUMP_MAP_PRESETS } from './MaterialDetailSettingsPanel';

interface ElementMaterialSetupPanelProps {
  elements: VdpElement[];
  onUpdateElementInTemplate?: (elementId: string, updates: Partial<VdpElement>) => void;
  highlightActiveElement: boolean;
  setHighlightActiveElement: (val: boolean) => void;
}

export const ElementMaterialSetupPanel: React.FC<ElementMaterialSetupPanelProps> = ({
  elements,
  onUpdateElementInTemplate,
  highlightActiveElement,
  setHighlightActiveElement
}) => {
  const threeD = useThreeDContext();
  const {
    elementMaterials,
    selectedElementIdForMaterial,
    setElementMaterial,
    clearElementMaterial,
    batchSetElementMaterials,
    setSelectedElementIdForMaterial
  } = threeD;

  const [copiedConfig, setCopiedConfig] = useState<ElementMaterialConfig | null>(null);
  const [copiedSuccess, setCopiedSuccess] = useState(false);

  // Filter out disabled or skipped elements
  const activeElements = elements.filter(el => !el.disabled && !el.isSkipped);

  // Automatically select first element if none is selected
  const activeElementId = selectedElementIdForMaterial || (activeElements.length > 0 ? activeElements[0].id : null);
  const selectedElement = activeElements.find(el => el.id === activeElementId);

  // Current config for selected element
  const currentConfig: ElementMaterialConfig = (activeElementId && elementMaterials[activeElementId]) || {
    finish: (selectedElement?.materialFinish as ElementMaterialFinish) || 'default',
    metalness: selectedElement?.materialMetalness,
    roughness: selectedElement?.materialRoughness,
    reliefHeight: selectedElement?.materialRelief,
    color: selectedElement?.materialColor,
    bumpMapType: selectedElement?.materialBumpMapType,
    bumpScale: selectedElement?.materialBumpScale,
    bumpRepeat: selectedElement?.materialBumpRepeat,
    invertBump: selectedElement?.materialInvertBump,
    bevelSoftness: selectedElement?.materialBevelSoftness
  };

  const activeFinishPreset = MATERIAL_FINISH_PRESETS.find(p => p.id === currentConfig.finish) || MATERIAL_FINISH_PRESETS[0];
  const effectiveMetalness = currentConfig.metalness !== undefined ? currentConfig.metalness : activeFinishPreset.defaultMetalness;
  const effectiveRoughness = currentConfig.roughness !== undefined ? currentConfig.roughness : activeFinishPreset.defaultRoughness;
  const effectiveRelief = currentConfig.reliefHeight !== undefined ? currentConfig.reliefHeight : activeFinishPreset.defaultRelief;
  const effectiveColor = currentConfig.color || activeFinishPreset.defaultColor;

  const effectiveBumpMap: ElementMaterialBumpMap = currentConfig.bumpMapType || (
    currentConfig.finish === 'embossed' || currentConfig.finish === 'debossed' ? 'smooth_bevel' : 'none'
  );
  const effectiveBumpScale = currentConfig.bumpScale !== undefined ? currentConfig.bumpScale : 1.0;
  const effectiveBumpRepeat = currentConfig.bumpRepeat !== undefined ? currentConfig.bumpRepeat : 6;
  const effectiveInvertBump = currentConfig.invertBump !== undefined ? currentConfig.invertBump : (currentConfig.finish === 'debossed');
  const effectiveBevelSoftness = currentConfig.bevelSoftness !== undefined ? currentConfig.bevelSoftness : 0.35;

  const [showBumpDetails, setShowBumpDetails] = useState(true);

  const handleSelectElement = (id: string) => {
    setSelectedElementIdForMaterial(id);
  };

  const handleApplyFinishPreset = (finishId: ElementMaterialFinish) => {
    if (!activeElementId) return;
    const preset = MATERIAL_FINISH_PRESETS.find(p => p.id === finishId) || MATERIAL_FINISH_PRESETS[0];
    const defaultBump: ElementMaterialBumpMap = 
      finishId === 'embossed' ? 'smooth_bevel' :
      finishId === 'debossed' ? 'smooth_bevel' :
      finishId === 'gold_foil' ? 'hammered_foil' : 'none';

    const newConfig: ElementMaterialConfig = {
      finish: finishId,
      metalness: preset.defaultMetalness,
      roughness: preset.defaultRoughness,
      reliefHeight: preset.defaultRelief,
      color: preset.defaultColor,
      bumpMapType: defaultBump,
      bumpScale: 1.0,
      bumpRepeat: 6,
      invertBump: finishId === 'debossed',
      bevelSoftness: 0.35
    };

    setElementMaterial(activeElementId, newConfig);

    if (onUpdateElementInTemplate) {
      onUpdateElementInTemplate(activeElementId, {
        materialFinish: finishId,
        materialMetalness: preset.defaultMetalness,
        materialRoughness: preset.defaultRoughness,
        materialRelief: preset.defaultRelief,
        materialColor: preset.defaultColor,
        materialBumpMapType: defaultBump,
        materialBumpScale: 1.0,
        materialBumpRepeat: 6,
        materialInvertBump: finishId === 'debossed',
        materialBevelSoftness: 0.35,
        materialConfig: newConfig
      });
    }
  };

  const handlePropertyChange = (property: keyof ElementMaterialConfig, value: any) => {
    if (!activeElementId) return;
    const updated = {
      ...currentConfig,
      [property]: value
    };
    // If tweaking metalness/roughness on default, promote to custom_metallic
    if (currentConfig.finish === 'default' && (property === 'metalness' || property === 'roughness')) {
      updated.finish = 'custom_metallic';
    }

    setElementMaterial(activeElementId, updated);

    if (onUpdateElementInTemplate) {
      onUpdateElementInTemplate(activeElementId, {
        ...(property === 'finish' ? { materialFinish: value } : {}),
        ...(property === 'metalness' ? { materialMetalness: value } : {}),
        ...(property === 'roughness' ? { materialRoughness: value } : {}),
        ...(property === 'reliefHeight' ? { materialRelief: value } : {}),
        ...(property === 'color' ? { materialColor: value } : {}),
        ...(property === 'bumpMapType' ? { materialBumpMapType: value } : {}),
        ...(property === 'bumpScale' ? { materialBumpScale: value } : {}),
        ...(property === 'bumpRepeat' ? { materialBumpRepeat: value } : {}),
        ...(property === 'invertBump' ? { materialInvertBump: value } : {}),
        ...(property === 'bevelSoftness' ? { materialBevelSoftness: value } : {}),
        materialConfig: updated
      });
    }
  };

  const handleResetCurrentElement = () => {
    if (!activeElementId) return;
    clearElementMaterial(activeElementId);
    if (onUpdateElementInTemplate) {
      onUpdateElementInTemplate(activeElementId, {
        materialFinish: 'default',
        materialMetalness: undefined,
        materialRoughness: undefined,
        materialRelief: undefined,
        materialColor: undefined,
        materialBumpMapType: 'none',
        materialBumpScale: 1.0,
        materialBumpRepeat: 6,
        materialInvertBump: false,
        materialBevelSoftness: 0.35,
        materialConfig: undefined
      });
    }
  };

  const handleBatchApplyType = (typeFilter: string, finishId: ElementMaterialFinish) => {
    const matching = activeElements.filter(el => {
      if (typeFilter === 'text') return ['text', 'currency', 'date', 'serial', 'uppercase'].includes(el.type);
      if (typeFilter === 'shapes') return ['rectangle', 'circle', 'star', 'polygon', 'line'].includes(el.type);
      if (typeFilter === 'codes') return ['qr', 'barcode', 'ean13', 'upca', 'datamatrix'].includes(el.type);
      return false;
    });

    const preset = MATERIAL_FINISH_PRESETS.find(p => p.id === finishId) || MATERIAL_FINISH_PRESETS[0];
    const newConfig: ElementMaterialConfig = {
      finish: finishId,
      metalness: preset.defaultMetalness,
      roughness: preset.defaultRoughness,
      reliefHeight: preset.defaultRelief,
      color: preset.defaultColor
    };

    const batchUpdates: Record<string, ElementMaterialConfig> = {};
    matching.forEach(el => {
      batchUpdates[el.id] = newConfig;
      if (onUpdateElementInTemplate) {
        onUpdateElementInTemplate(el.id, {
          materialFinish: finishId,
          materialMetalness: preset.defaultMetalness,
          materialRoughness: preset.defaultRoughness,
          materialRelief: preset.defaultRelief,
          materialColor: preset.defaultColor,
          materialConfig: newConfig
        });
      }
    });

    batchSetElementMaterials(batchUpdates);
  };

  const handleResetAllElements = () => {
    const emptyBatch: Record<string, ElementMaterialConfig> = {};
    activeElements.forEach(el => {
      emptyBatch[el.id] = { finish: 'default' };
      if (onUpdateElementInTemplate) {
        onUpdateElementInTemplate(el.id, {
          materialFinish: 'default',
          materialMetalness: undefined,
          materialRoughness: undefined,
          materialRelief: undefined,
          materialColor: undefined,
          materialConfig: undefined
        });
      }
    });
    batchSetElementMaterials(emptyBatch);
  };

  const handleCopyFinish = () => {
    setCopiedConfig(currentConfig);
    setCopiedSuccess(true);
    setTimeout(() => setCopiedSuccess(false), 2000);
  };

  const handlePasteFinish = () => {
    if (!copiedConfig || !activeElementId) return;
    setElementMaterial(activeElementId, copiedConfig);
    if (onUpdateElementInTemplate) {
      onUpdateElementInTemplate(activeElementId, {
        materialFinish: copiedConfig.finish,
        materialMetalness: copiedConfig.metalness,
        materialRoughness: copiedConfig.roughness,
        materialRelief: copiedConfig.reliefHeight,
        materialColor: copiedConfig.color,
        materialConfig: copiedConfig
      });
    }
  };

  const getElementIcon = (type: string) => {
    if (['text', 'currency', 'date', 'serial', 'uppercase'].includes(type)) return <Type className="w-3.5 h-3.5 text-blue-400" />;
    if (type === 'rectangle' || type === 'circle' || type === 'star' || type === 'polygon') return <Square className="w-3.5 h-3.5 text-purple-400" />;
    if (type === 'qr') return <QrCode className="w-3.5 h-3.5 text-emerald-400" />;
    if (type === 'barcode' || type === 'ean13' || type === 'upca') return <Barcode className="w-3.5 h-3.5 text-amber-400" />;
    if (type === 'image') return <ImageIcon className="w-3.5 h-3.5 text-pink-400" />;
    return <Layers className="w-3.5 h-3.5 text-zinc-400" />;
  };

  const getElementLabel = (el: VdpElement) => {
    const text = el.content || el.text || el.staticValue || (el as any).name;
    if (text) return `"${String(text).slice(0, 18)}${String(text).length > 18 ? '…' : ''}"`;
    if (el.columnName) return `[${el.columnName}]`;
    return `${el.type.toUpperCase()}`;
  };

  return (
    <div className="space-y-3.5 text-zinc-200">
      {/* Overview Banner */}
      <div className="p-2.5 rounded-xl bg-gradient-to-r from-amber-500/15 via-purple-500/10 to-indigo-500/15 border border-amber-500/30 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <div className="p-1 rounded-lg bg-amber-500/20 text-amber-300 mt-0.5 shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h5 className="text-[11px] font-bold text-amber-200 leading-tight">
              Per-Element Finishes &amp; Material Shaders
            </h5>
            <p className="text-[9.5px] text-zinc-400 leading-normal mt-0.5">
              Select any element on your sticker to apply 24K Gold, Chrome Silver, Spot Gloss, or 3D Emboss/Deboss.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setHighlightActiveElement(!highlightActiveElement)}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[9.5px] font-bold border transition-colors cursor-pointer shrink-0 ${
            highlightActiveElement
              ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
              : 'bg-zinc-900 text-zinc-400 border-zinc-700/60 hover:text-zinc-200'
          }`}
          title="Toggle active element contour outline in 3D viewport"
        >
          {highlightActiveElement ? <Eye className="w-3 h-3 text-amber-400" /> : <EyeOff className="w-3 h-3" />}
          <span>3D Outline</span>
        </button>
      </div>

      {/* 1. Element Selector Picker */}
      <div className="space-y-1.5 bg-zinc-900/80 p-2.5 rounded-xl border border-zinc-800">
        <div className="flex items-center justify-between">
          <label className="text-[11px] font-bold text-zinc-300 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-indigo-400" />
            <span>Select Particular Element ({activeElements.length}):</span>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleResetAllElements}
              className="text-[9.5px] text-zinc-400 hover:text-zinc-200 hover:underline cursor-pointer flex items-center gap-0.5"
              title="Reset all elements to base label finish"
            >
              <RotateCcw className="w-2.5 h-2.5" />
              <span>Reset All</span>
            </button>
          </div>
        </div>

        {/* Dropdown Selector */}
        <select
          value={activeElementId || ''}
          onChange={(e) => handleSelectElement(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-700 text-white text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-amber-500 font-medium"
        >
          {activeElements.map(el => {
            const config = elementMaterials[el.id] || el.materialConfig;
            const finishName = config?.finish && config.finish !== 'default'
              ? ` [✨ ${MATERIAL_FINISH_PRESETS.find(p => p.id === config.finish)?.name || config.finish}]`
              : ' [Base Paper]';
            return (
              <option key={el.id} value={el.id}>
                {el.type.toUpperCase()}: {getElementLabel(el)}{finishName}
              </option>
            );
          })}
        </select>

        {/* Quick Clickable Element Strip */}
        <div className="flex items-center gap-1 overflow-x-auto pt-1 pb-0.5 custom-scrollbar">
          {activeElements.map(el => {
            const isSelected = el.id === activeElementId;
            const config = elementMaterials[el.id] || el.materialConfig;
            const finishId = config?.finish || 'default';
            const preset = MATERIAL_FINISH_PRESETS.find(p => p.id === finishId);
            const hasCustomFinish = finishId !== 'default';

            return (
              <button
                key={el.id}
                type="button"
                onClick={() => handleSelectElement(el.id)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-semibold border transition-all shrink-0 cursor-pointer ${
                  isSelected
                    ? 'bg-amber-500/20 border-amber-500 text-amber-200 shadow-sm'
                    : hasCustomFinish
                    ? 'bg-purple-950/40 border-purple-800/80 text-purple-200 hover:border-purple-600'
                    : 'bg-zinc-950/70 border-zinc-800 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {getElementIcon(el.type)}
                <span className="truncate max-w-[90px]">{getElementLabel(el)}</span>
                {hasCustomFinish && (
                  <span className="text-[9px] px-1 py-0.2 rounded bg-amber-500/30 text-amber-300 font-bold">
                    {preset?.icon || '✨'}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Quick Batch Type Buttons */}
        <div className="flex items-center justify-between pt-1 text-[9.5px] text-zinc-400 border-t border-zinc-800/60">
          <span>Batch Gold Foil:</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleBatchApplyType('text', 'gold_foil')}
              className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-amber-950/40 hover:text-amber-300 border border-zinc-700 hover:border-amber-700 transition-colors cursor-pointer"
            >
              All Text
            </button>
            <button
              type="button"
              onClick={() => handleBatchApplyType('shapes', 'gold_foil')}
              className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-amber-950/40 hover:text-amber-300 border border-zinc-700 hover:border-amber-700 transition-colors cursor-pointer"
            >
              All Shapes
            </button>
            <button
              type="button"
              onClick={() => handleBatchApplyType('codes', 'spot_gloss')}
              className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-cyan-950/40 hover:text-cyan-300 border border-zinc-700 hover:border-cyan-700 transition-colors cursor-pointer"
            >
              Gloss Barcodes
            </button>
          </div>
        </div>
      </div>

      {/* 2. Finish & Material Effect Preset Grid */}
      {selectedElement && (
        <div className="space-y-2 bg-zinc-900/60 p-3 rounded-xl border border-zinc-800">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-amber-300 flex items-center gap-1.5">
              <Palette className="w-3.5 h-3.5 text-amber-400" />
              <span>Material Finish for {getElementLabel(selectedElement)}:</span>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleCopyFinish}
                className="flex items-center gap-1 text-[9.5px] text-zinc-400 hover:text-amber-300 cursor-pointer font-medium"
                title="Copy current material config"
              >
                {copiedSuccess ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedSuccess ? 'Copied' : 'Copy'}</span>
              </button>
              {copiedConfig && (
                <button
                  type="button"
                  onClick={handlePasteFinish}
                  className="text-[9.5px] text-indigo-400 hover:text-indigo-300 cursor-pointer font-bold ml-1"
                  title="Paste copied finish onto this element"
                >
                  Paste
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            {MATERIAL_FINISH_PRESETS.map((preset) => {
              const isActive = currentConfig.finish === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleApplyFinishPreset(preset.id)}
                  className={`flex flex-col items-center justify-center p-2 rounded-xl border text-center transition-all cursor-pointer ${
                    isActive
                      ? 'bg-amber-500/20 border-amber-400 text-white shadow-md ring-1 ring-amber-500/50'
                      : 'bg-zinc-950/70 border-zinc-800/80 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                  }`}
                >
                  <span className="text-base mb-0.5">{preset.icon}</span>
                  <span className="text-[10px] font-bold leading-tight line-clamp-1">{preset.name.replace(/^(24K|Chrome|Raised|Soft-Touch|3D|Holographic)\s*/, '')}</span>
                  <span className="text-[8.5px] text-zinc-500 font-mono mt-0.5">{preset.sheen}</span>
                </button>
              );
            })}
          </div>

          <p className="text-[9.5px] text-zinc-400 italic px-1">
            {activeFinishPreset.description}
          </p>

          {/* 3. Granular PBR Surface Properties & Relief Setup */}
          <div className="pt-2.5 border-t border-zinc-800/80 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-zinc-300 flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-cyan-400" />
                <span>Fine-Tune Material Properties</span>
              </span>
              <button
                type="button"
                onClick={handleResetCurrentElement}
                className="text-[9.5px] text-zinc-400 hover:text-rose-400 cursor-pointer font-medium"
              >
                Reset to Base
              </button>
            </div>

            {/* Foil / Varnish Tint Color Swatches */}
            {['gold_foil', 'silver_foil', 'custom_metallic', 'holographic'].includes(currentConfig.finish) && (
              <div className="space-y-1.5 bg-zinc-950/80 p-2 rounded-lg border border-zinc-800">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="font-semibold text-zinc-300">Foil Tint / Luster Tone:</span>
                  <span className="font-mono text-amber-400 font-bold">{effectiveColor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={effectiveColor}
                    onChange={(e) => handlePropertyChange('color', e.target.value)}
                    className="w-7 h-7 rounded bg-transparent border border-zinc-700 cursor-pointer shrink-0"
                  />
                  <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-0.5">
                    {QUICK_FOIL_SWATCHES.map((sw) => (
                      <button
                        key={sw.name}
                        type="button"
                        onClick={() => handlePropertyChange('color', sw.color)}
                        className={`w-4 h-4 rounded-full border transition-transform shrink-0 ${
                          effectiveColor.toLowerCase() === sw.color.toLowerCase()
                            ? 'scale-125 border-white ring-2 ring-amber-500'
                            : 'border-zinc-700 hover:scale-110'
                        }`}
                        style={{ backgroundColor: sw.color }}
                        title={sw.name}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Metalness / Metallic Slider */}
            <div className="space-y-1">
              <div className="flex justify-between text-[10.5px] text-zinc-400 font-mono">
                <span className="flex items-center gap-1">
                  <span>Metalness / Specularity:</span>
                </span>
                <span className="text-amber-300 font-bold">{Math.round(effectiveMetalness * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={effectiveMetalness}
                onChange={(e) => handlePropertyChange('metalness', parseFloat(e.target.value))}
                className="w-full accent-amber-500 cursor-pointer"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500">
                <span>Non-metal (Paper/Ink)</span>
                <span>Chrome/Foil (100%)</span>
              </div>
            </div>

            {/* Roughness / Gloss Slider */}
            <div className="space-y-1">
              <div className="flex justify-between text-[10.5px] text-zinc-400 font-mono">
                <span className="flex items-center gap-1">
                  <span>Roughness / Surface Sheen:</span>
                </span>
                <span className="text-cyan-300 font-bold">{Math.round(effectiveRoughness * 100)}% ({effectiveRoughness < 0.2 ? 'Mirror Gloss' : effectiveRoughness > 0.7 ? 'Matte' : 'Satin'})</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.02"
                value={effectiveRoughness}
                onChange={(e) => handlePropertyChange('roughness', parseFloat(e.target.value))}
                className="w-full accent-cyan-500 cursor-pointer"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500">
                <span>0% Mirror Shine</span>
                <span>100% Diffuse Matte</span>
              </div>
            </div>

            {/* 3D Tactile Relief Depth / Height (Emboss / Deboss) */}
            <div className="space-y-1 bg-purple-950/20 p-2 rounded-lg border border-purple-900/40">
              <div className="flex justify-between text-[10.5px] text-purple-200 font-mono">
                <span className="font-semibold flex items-center gap-1">
                  <span>3D Relief Depth / Displacement:</span>
                </span>
                <span className={`font-bold ${effectiveRelief > 0 ? 'text-emerald-400' : effectiveRelief < 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                  {effectiveRelief > 0 ? `+${effectiveRelief.toFixed(2)} mm (Emboss)` : effectiveRelief < 0 ? `${effectiveRelief.toFixed(2)} mm (Deboss)` : '0.00 mm (Flat)'}
                </span>
              </div>
              <input
                type="range"
                min="-2.0"
                max="2.0"
                step="0.05"
                value={effectiveRelief}
                onChange={(e) => handlePropertyChange('reliefHeight', parseFloat(e.target.value))}
                className="w-full accent-purple-500 cursor-pointer"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500">
                <span className="text-amber-400/80">-2mm Deboss</span>
                <span>0mm Flat</span>
                <span className="text-emerald-400/80">+2mm Emboss</span>
              </div>
            </div>

            {/* DETAIL SETTINGS: Bump Maps & Tactile Embossing Texture */}
            <div className="pt-2 border-t border-zinc-800 space-y-2.5">
              <div 
                onClick={() => setShowBumpDetails(!showBumpDetails)}
                className="flex items-center justify-between cursor-pointer select-none text-zinc-300 hover:text-white"
              >
                <div className="flex items-center gap-1.5 font-bold text-[11px]">
                  <Layers className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Tactile Bump Map & Texture Details</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[9.5px] font-mono text-indigo-400 font-semibold">
                    {BUMP_MAP_PRESETS.find(b => b.id === effectiveBumpMap)?.name || 'Custom'}
                  </span>
                  {showBumpDetails ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                </div>
              </div>

              {showBumpDetails && (
                <div className="space-y-2.5 bg-zinc-950/70 p-2.5 rounded-lg border border-zinc-800/80">
                  {/* Bump Texture Selector Dropdown */}
                  <div>
                    <label className="text-[10px] text-zinc-400 block mb-1 font-medium">
                      Surface Bump Texture Pattern:
                    </label>
                    <select
                      value={effectiveBumpMap}
                      onChange={(e) => handlePropertyChange('bumpMapType', e.target.value as ElementMaterialBumpMap)}
                      className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-indigo-500 font-medium"
                    >
                      {BUMP_MAP_PRESETS.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.icon} {b.name} - {b.desc}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Bump Scale / Height Intensity */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">Bump Height Intensity:</span>
                      <span className="text-indigo-400 font-bold">{(effectiveBumpScale * 100).toFixed(0)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="2.0"
                      step="0.05"
                      value={effectiveBumpScale}
                      onChange={(e) => handlePropertyChange('bumpScale', parseFloat(e.target.value))}
                      className="w-full accent-indigo-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                    />
                    <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                      <span>Subtle (0%)</span>
                      <span>Standard (100%)</span>
                      <span>Heavy (200%)</span>
                    </div>
                  </div>

                  {/* Bump Repeat / Frequency */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">Tiling Density:</span>
                      <span className="text-indigo-300 font-bold">{effectiveBumpRepeat}× Frequency</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={effectiveBumpRepeat}
                      onChange={(e) => handlePropertyChange('bumpRepeat', parseInt(e.target.value))}
                      className="w-full accent-indigo-400 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>

                  {/* Invert Bump & Bevel Softness Row */}
                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-800/80">
                    <div className="flex items-center justify-between p-2 rounded-lg bg-zinc-900 border border-zinc-800">
                      <label className="text-[10px] text-zinc-300 font-medium">Invert Bump:</label>
                      <input
                        type="checkbox"
                        checked={effectiveInvertBump}
                        onChange={(e) => handlePropertyChange('invertBump', e.target.checked)}
                        className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                      />
                    </div>

                    <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 space-y-1">
                      <div className="flex justify-between text-[9.5px] font-mono">
                        <span className="text-zinc-400">Bevel Softness:</span>
                        <span className="text-zinc-300 font-bold">{Math.round(effectiveBevelSoftness * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={effectiveBevelSoftness}
                        onChange={(e) => handlePropertyChange('bevelSoftness', parseFloat(e.target.value))}
                        className="w-full accent-zinc-400 cursor-pointer h-1 bg-zinc-800 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
